import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarQuickFilter } from '@mui/x-data-grid';
import RangedDatePicker from '../DatePicker/RangedDatePicker';
import { Grid } from '@mui/material';

/**
 *
 * @param {import('../../../../types/common').PaginationOptions} paginationOptions
 * @returns
 */
export const spreadPaginationOptions = (paginationOptions) => {
  return {
    ...(paginationOptions.fromDate ? { fromDate: paginationOptions.fromDate.toISOString() } : {}),
    ...(paginationOptions.toDate ? { toDate: paginationOptions.toDate.toISOString() } : {}),
    ...(typeof paginationOptions.page === 'number' ? { page: paginationOptions.page } : {}),
    ...(typeof paginationOptions.pageSize === 'number' ? { pageSize: paginationOptions.pageSize } : {}),
    ...(paginationOptions.orderBy ? { orderBy: paginationOptions.orderBy } : {}),
    ...(paginationOptions.orderDirection ? { orderDirection: paginationOptions.orderDirection } : {}),
    ...(paginationOptions.searchTerms ? { searchTerms: paginationOptions.searchTerms } : {}),
  };
};

const stringFilter = (value) => {
  return (params) => {
    return params.value.toString().includes(value);
  };
};

const TableToolbar = (props) => {
  const { searchConfig, dateConfig, isExportable, toolbarExtension } = props;
  const { isSearchBar } = searchConfig;
  const { isDateFiltering, fromDate, setFromDate, toDate, setToDate } = dateConfig;

  return (
    <GridToolbarContainer sx={{ margin: '10px' }}>
      <Grid container rowSpacing={2} columnSpacing={2} justifyContent="flex-start" alignItems="flex-end">
        {isSearchBar && (
          <Grid item xs={8} md={4}>
            <GridToolbarQuickFilter sx={{ width: '100%' }} />
          </Grid>
        )}

        {isExportable && (
          <Grid item xs={4} md={2}>
            <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
          </Grid>
        )}

        {isDateFiltering && (
          <Grid item xs={12} md={6}>
            <RangedDatePicker
              id="ranged-date-picker"
              fromValue={fromDate}
              toValue={toDate}
              fromOnChange={setFromDate}
              toOnChange={setToDate}
              closeOnSelect
              format="MM/dd/yyyy"
            />
          </Grid>
        )}

        {toolbarExtension && (
          <Grid item xs={12}>
            {toolbarExtension}
          </Grid>
        )}
      </Grid>
    </GridToolbarContainer>
  );
};

TableToolbar.propTypes = {
  dateConfig: PropTypes.shape({
    isDateFiltering: PropTypes.bool, // display date range picker
    fromDate: PropTypes.instanceOf(Date),
    setFromDate: PropTypes.func,
    toDate: PropTypes.instanceOf(Date),
    setToDate: PropTypes.func,
  }),
  searchConfig: PropTypes.shape({
    isSearchBar: PropTypes.bool,
  }),
  isExportable: PropTypes.bool,
  toolbarExtension: PropTypes.any,
};

const TableV2 = (props) => {
  const {
    rows = [],
    columns = [],
    fetchData,
    defaultSortColumn,
    searchConfig = { isSearchBar: false },
    dateConfig = { isDateFiltering: false },
    isExportable = false,
    toolbarExtension,
    defaultRowsPerPage = 10,
    rowCount,
    isLoading = false,
  } = props;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: defaultRowsPerPage,
  });
  const [sortModel, setSortModel] = useState(defaultSortColumn ? [{ field: defaultSortColumn, sort: 'desc' }] : []);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const getInitialFromDate = () => {
    const date = new Date();
    let days = 30;
    if (dateConfig?.defaultFromDateDays && !Number.isNaN(dateConfig.defaultFromDateDays)) {
      days = dateConfig.defaultFromDateDays;
    }
    return new Date(date.setDate(date.getDate() - days));
  };
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(new Date());

  useEffect(() => {
    if (typeof fetchData === 'function') {
      let orderBy = null;
      let orderDirection = null;
      if (sortModel?.length > 0) {
        orderBy = sortModel[0].field;
        orderDirection = sortModel[0].sort;
      }

      const page = paginationModel.page;
      const pageSize = paginationModel.pageSize;
      const searchTerms = filterModel.quickFilterValues;

      const options = { page, pageSize, searchTerms, orderBy, orderDirection };
      if (dateConfig?.isDateFiltering) {
        options.fromDate = fromDate;
        options.toDate = toDate;
      }

      fetchData(options);
    }
  }, [filterModel, paginationModel, sortModel, fromDate, toDate]);

  useEffect(() => {
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [filterModel, fromDate, toDate]);

  const appliedColumns = useMemo(() => {
    if (searchConfig.isSearchBar) {
      return columns
        .filter((column) => columnVisibilityModel[column.field] !== false) // filter for visible columns only - visible is not included in list
        .map((column) => {
          if (searchConfig.onlySearchableColumn === column.field) {
            return {
              getApplyQuickFilterFn: stringFilter,
              ...column, // allow overwrite of filter in column specification if needed
            };
          }
          return column;
        });
    }
    return columns;
  }, [columns, columnVisibilityModel, searchConfig.onlySearchableColumn, searchConfig.searchBar]);

  return (
    <>
      <DataGrid
        autoHeight
        rows={rows}
        getRowId={(row) => (row.id ? row.id : row._id)}
        columns={appliedColumns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => {
          setColumnVisibilityModel(newModel);
        }}
        loading={isLoading}
        slots={{ toolbar: TableToolbar }}
        slotProps={{
          toolbar: {
            dateConfig: { ...dateConfig, fromDate, setFromDate, toDate, setToDate },
            searchConfig,
            isExportable,
            toolbarExtension,
          },
        }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        localeText={{
          footerRowSelected: () => '', // Removes "X row(s) selected" text
        }}
        rowCount={rowCount}
        paginationMode="server"
        pageSizeOptions={[5, 10, 20]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
      />
    </>
  );
};

TableV2.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired, // must have unique field named id.
  fetchData: PropTypes.func.isRequired,
  rowCount: PropTypes.number,
  isLoading: PropTypes.bool, // turns on loading overlay within table only
  isExportable: PropTypes.bool,
  defaultSortColumn: PropTypes.string, // same field name as column you would like to sort by default.
  searchConfig: PropTypes.shape({
    isSearchBar: PropTypes.bool, // display search bar
    onlySearchableColumn: PropTypes.string, // field name of column of data the search bar searches. If none included, search filter will apply to all visible columns.
  }),
  dateConfig: PropTypes.shape({
    isDateFiltering: PropTypes.bool, // display date range picker
    defaultFromDateDays: PropTypes.number, // number of days in the past to set the default from date
  }),
  toolbarExtension: PropTypes.any, // JSX child to be rendered in the toolbar. Managed by parent component. Used for adding buttons.
  defaultRowsPerPage: PropTypes.number, // number of rows to display by default (must be one of 5, 10, 20 or it removes options to select from)
};

export default TableV2;
